import React, { useState, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import moment from 'moment';

import { StackableModal, FilterDropdown } from '../components/nextgen';
import Button from '../components/Button';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import { getPMSchedules } from '../api/pmSchedules';
import { StatusBadge } from '../components/nextgen/Badge';

const MODAL_NAME = 'pmSchedulePicker';

const PMSchedulePickerModal = () => {
	const { open: options, setClose } = useModal(MODAL_NAME);
	const [selectedSchedules, setSelectedSchedules] = useState([]);
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');

	const { value: schedules, execute: fetchSchedules, status } = useAsync(
		getPMSchedules,
		{ immediate: false }
	);

	useEffect(() => {
		if (options) {
			fetchSchedules({});
			setSelectedSchedules([]);
			setSearch('');
			setStatusFilter('all');
		}
	}, [options, fetchSchedules]);

	const handleSelect = (schedule) => {
		if (options?.multiSelect) {
			if (selectedSchedules.find((s) => s.id === schedule.id)) {
				setSelectedSchedules(selectedSchedules.filter((s) => s.id !== schedule.id));
			} else {
				const maxSelect = options?.maxSelect || Infinity;
				if (selectedSchedules.length < maxSelect) {
					setSelectedSchedules([...selectedSchedules, schedule]);
				}
			}
		} else {
			setSelectedSchedules([schedule]);
		}
	};

	const handleConfirm = () => {
		if (options?.onSelect && selectedSchedules.length > 0) {
			options.onSelect(selectedSchedules);
		}
		setClose();
	};

	const filteredSchedules = schedules?.rows?.filter((schedule) => {
		// Search filter
		if (search) {
			const searchLower = search.toLowerCase();
			const matchesSearch =
				schedule?.name?.toLowerCase().includes(searchLower) ||
				schedule?.frequency?.toLowerCase().includes(searchLower);
			if (!matchesSearch) return false;
		}

		// Status filter
		if (statusFilter !== 'all') {
			const isActive = !!schedule?.active;
			if (statusFilter === 'active' && !isActive) return false;
			if (statusFilter === 'inactive' && isActive) return false;
		}

		return true;
	}) || [];

	// Filter out schedules that are already attached to the asset
	const skip = options?.skip || [];
	const availableSchedules = filteredSchedules.filter(
		(schedule) => !skip.includes(schedule.id)
	);

	const isLoading = status === 'pending';

	return (
		<StackableModal
			open={!!options}
			onClose={setClose}
			title={options?.title || 'Select PM Schedules'}
			size="2xl"
		>
			<div className="space-y-4">
				{/* Search and Filters */}
				<div className="flex gap-3">
					<input
						type="text"
						placeholder="Search PM schedules..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
					/>
					<FilterDropdown
					label="Status"
					placeholder="All Statuses"
					value={statusFilter}
					options={[
						{ value: 'all', label: 'All Statuses' },
						{ value: 'active', label: 'Active' },
						{ value: 'inactive', label: 'Inactive' },
					]}
					onChange={setStatusFilter}
				/>
				</div>

				{/* Schedule List */}
				<div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
					{isLoading ? (
						<div className="p-8 text-center text-gray-500">
							Loading PM schedules...
						</div>
					) : availableSchedules.length === 0 ? (
						<div className="p-8 text-center text-gray-500">
							No PM schedules found
						</div>
					) : (
						<ul className="divide-y divide-gray-200">
							{availableSchedules.map((schedule) => {
								const isSelected = selectedSchedules.find(
									(s) => s.id === schedule.id
								);
								const nextDue = schedule?.nextDueDate
									? moment(schedule.nextDueDate)
									: null;

								return (
									<li
										key={schedule.id}
										onClick={() => handleSelect(schedule)}
										className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
											isSelected ? 'bg-indigo-50' : ''
										}`}
									>
										<div className="flex items-start justify-between">
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 mb-1">
													<p className="text-sm font-medium text-gray-900 truncate">
														{schedule?.name || 'Untitled Schedule'}
													</p>
													<StatusBadge
														color={schedule?.active ? 'green' : 'gray'}
													>
														{schedule?.active ? 'Active' : 'Inactive'}
													</StatusBadge>
												</div>
												<p className="text-xs text-gray-500">
													{schedule?.frequency && (
														<span className="mr-3 capitalize">
															{schedule.frequency.replace('_', ' ')}
														</span>
													)}
													{nextDue && (
														<span>
															Next: {nextDue.format('MMM D, YYYY')}
														</span>
													)}
												</p>
											</div>
											{isSelected && (
												<CheckIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 ml-2" />
											)}
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</div>

				{/* Footer */}
				<div className="flex justify-between items-center pt-4">
					<p className="text-sm text-gray-500">
						{selectedSchedules.length > 0 && (
							<>
								{selectedSchedules.length} schedule{selectedSchedules.length !== 1 ? 's' : ''} selected
								{options?.maxSelect && ` (max ${options.maxSelect})`}
							</>
						)}
					</p>
					<div className="flex gap-3">
						<Button type="secondary" onClick={setClose}>
							Cancel
						</Button>
						<Button
							onClick={handleConfirm}
							disabled={selectedSchedules.length === 0}
						>
							Add {selectedSchedules.length > 0 && `(${selectedSchedules.length})`}
						</Button>
					</div>
				</div>
			</div>
		</StackableModal>
	);
};

export default PMSchedulePickerModal;
